import { TabsContent } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import axios from '../../../lib/axios';
import { useParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { PhoneNumberInput } from "@/components/ui/phone-number-input";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsUserAdmin } from "@/hooks/authHelpers";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

export default function DialerTab() {
    const { id: communityId } = useParams();
    const { toast } = useToast();
    const [settings, setSettings] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [alternativePhoneNumber, setAlternativePhoneNumber] = useState(null);

    const isAdmin = useIsUserAdmin();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const communityResponse = await axios.get(`/communities/${communityId}/settings`);
                setSettings(communityResponse.data);
                setAlternativePhoneNumber(communityResponse?.data?.alternative_callback_forwarding_number);
            } catch (error) {
                console.error('Error fetching data:', error);
                toast({
                    title: "Error",
                    description: "Failed to load community details. Please try again.",
                    variant: "destructive",
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [communityId, toast]);

    const handleChange = async (data) => {
        setIsLoading(true);

        try {
            const response = await axios.patch(`/communities/${communityId}/settings`, {
                ...settings,
                ...data,
            });

            setSettings({ ...response.data });

            toast({
                title: "Success",
                description: "Updated community settings!",
            });
        } catch (error) {
            console.error('Failed to update settings:', error);
            toast({
                title: "Error",
                description: "Failed to update settings.",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleShouldAnnounceChange = (checked) => {
        if (!isAdmin) {
            return;
        }

        handleChange({ should_announce_company_when_calling: checked });
    };

    const handleHasSendSMSIfProspectDoesntAnswer = (checked) => {
        if (!isAdmin) {
            return;
        }

        handleChange({ has_send_sms_if_prospect_doesnt_answer: checked });
    };

    const handleNumberOfTimesToRepeatFlairPrompt = async (event) => {
        if (!isAdmin) {
            return;
        }

        const { value } = event.target;

        if (value > 15) {
            return;
        }

        if (value < 1) {
            return;
        }

        handleChange({ number_of_times_to_repeat_flair_prompt: value });
    };

    const handleReattemptWithSecondaryPhoneNumber = async (checked) => {
        if (!isAdmin) {
            return;
        }

        handleChange({ should_reattempt_with_secondary_phone_number: checked });
    };

    const handleHasRetryQueue = (checked) => {
        if (!isAdmin) {
            return;
        }

        handleChange({ has_retry_queue: checked });
    };

    const handleRetryQueueInterval = (event) => {
        if (!isAdmin) {
            return;
        }

        const { value } = event.target;
        if (value < 1 || value > 60) {
            return;
        }

        handleChange({ retry_queue_interval: value });
    };

    const handleAlternateNumber = (e) => {
        const { value } = e.target;
        setAlternativePhoneNumber(value)
    };

    const handleSaveNumber = async () => {
        handleChange({ alternative_callback_forwarding_number: alternativePhoneNumber });
    };

    if (isLoading && !settings?.community_id) {
        /* one‑liner row (label + toggle) */
        const RowSkeleton = () => (
            <div className="flex items-center justify-between space-x-2">
                <div className="space-y-2 w-full">
                    <Skeleton className="h-4 w-48" /> {/* fake label */}
                    <Skeleton className="h-3 w-full max-w-md" /> {/* fake hint */}
                </div>
                <Skeleton className="h-6 w-12 rounded-full" /> {/* fake switch */}
            </div>
        );

        return (
            <TabsContent value="dialer">
                <div className="grid grid-cols-1 gap-6 mt-4">
                    {/* 3 switch rows + number input + phone input */}
                    {Array.from({ length: 3 }).map((_, i) => (
                        <RowSkeleton key={i} />
                    ))}

                    {/* number input skeleton */}
                    <div className="flex items-center justify-between space-x-2">
                        <div className="space-y-2 w-full">
                            <Skeleton className="h-4 w-72" />
                            <Skeleton className="h-3 w-full max-w-md" />
                        </div>
                        <Skeleton className="h-10 w-20 rounded-md" />
                    </div>

                    {/* phone number input skeleton */}
                    <div className="space-y-2">
                        <Skeleton className="h-4 w-72" />
                        <Skeleton className="h-3 w-full max-w-md" />
                        <Skeleton className="h-10 w-72 rounded-md" />
                    </div>
                </div>
            </TabsContent>
        );
    }



    return (
        <TabsContent value="dialer" className="grid grid-cols-1 gap-10 mt-4">
            <div className="grid grid-cols-2 gap-2">
                <Label htmlFor="announce-company" className="flex flex-col space-y-1 justify-self-start">
                    <span>Announce Company Name</span>
                    <span className="text-sm text-muted-foreground">
                        When enabled, the company name will be announced during calls.
                    </span>
                </Label>
                <Switch
                    id="announce-company"
                    checked={settings.should_announce_company_when_calling}
                    onCheckedChange={handleShouldAnnounceChange}
                    disabled={isLoading || !isAdmin || settings?.community?.status !== 'active'}
                    className="justify-self-end"
                />
            </div>
            <Separator/>
            <div className="grid grid-cols-2 gap-2">
                <Label htmlFor="has-send-sms-if-prospect-doesnt-answe" className="flex flex-col space-y-1 justify-self-start">
                    <span>Text Prospect If Unanswered</span>
                    <span className="text-sm text-muted-foreground">
                        When enabled, the prospect will receive a text notifying them to callback the community.
                    </span>
                </Label>
                <Switch
                    id="has-send-sms-if-prospect-doesnt-answe"
                    checked={settings.has_send_sms_if_prospect_doesnt_answer}
                    onCheckedChange={handleHasSendSMSIfProspectDoesntAnswer}
                    disabled={isLoading || !isAdmin || settings?.community?.status !== 'active'}
                    className="justify-self-end"
                />
            </div>
            <Separator/>
            <div className="grid grid-cols-2 gap-2">
                <Label htmlFor="reattempt-with-secondary-phone-number" className="flex flex-col space-y-1 justify-self-start">
                    <span>Reattempt with secondary phone number</span>
                    <span className="text-sm text-muted-foreground">
                        When enabled, if a phone call trying to reach the community phone number fails, we will reattempt by using the secondary phone number.
                    </span>
                </Label>
                <Switch
                    id="reattempt-with-secondary-phone-number"
                    checked={settings.should_reattempt_with_secondary_phone_number}
                    onCheckedChange={handleReattemptWithSecondaryPhoneNumber}
                    disabled={isLoading || !isAdmin || settings?.community?.status !== 'active'}
                    className="justify-self-end"
                />
            </div>
            <Separator/>
            <div className="grid grid-cols-2 gap-2">
                <Label htmlFor="has-retry-queue" className="flex flex-col space-y-1 justify-self-start">
                    <span>Call retry</span>
                    <span className="text-sm text-muted-foreground">
                        When enabled, missed calls will be retried once after the interval.
                    </span>
                </Label>
                <Switch
                    id="has-retry-queue"
                    checked={settings.has_retry_queue}
                    onCheckedChange={handleHasRetryQueue}
                    disabled={isLoading || !isAdmin || settings?.community?.status !== 'active'}
                    className="justify-self-end"
                />
            </div>
            <div className="grid grid-cols-2 gap-2">
                <Label htmlFor="retry-queue-interval" className="flex flex-col space-y-1 justify-self-start">
                    <span>Retry interval (minutes)</span>
                    <span className="text-sm text-muted-foreground">1-60 minutes (Call retry must be enabled)</span>
                </Label>
                <Input
                    type="number"
                    id="retry-queue-interval"
                    value={settings.retry_queue_interval}
                    onChange={handleRetryQueueInterval}
                    disabled={isLoading || !isAdmin || settings?.community?.status !== 'active'}
                    className="w-20 text-sm px-2 py-1 justify-self-end"
                />
            </div>
            <Separator/>
            <div className="grid grid-cols-2 gap-2">
                <Label htmlFor="number-of-times-to-repeat-flair-prompt" className="flex flex-col space-y-1 justify-self-start">
                    <span>Number of times to repeat Flair prompt</span>
                    <span className="text-sm text-muted-foreground">
                        Default loop is 3 times and will repeat waiting for community response to press 1.
                        After all repeats are finished, and there is no response of 1, Flair will hang up.
                    </span>
                </Label>
                <Input
                    type="number"
                    id="number-of-times-to-repeat-flair-prompt"
                    value={settings.number_of_times_to_repeat_flair_prompt}
                    onChange={handleNumberOfTimesToRepeatFlairPrompt}
                    disabled={isLoading || !isAdmin || settings?.community?.status !== 'active'}
                    className="w-20 text-sm px-2 py-1 justify-self-end"
                />
            </div>
            <Separator/>
            <div className="grid md:grid-cols-2 gap-2 grid-cols-1">
                <Label htmlFor="alternate_number" className="flex flex-col space-y-1 mb-2">
                    <span>Alternative Callback Forwarding Number</span>
                    <span className="text-sm text-muted-foreground">
                        This number will be used when a prospect calls back. If no number is specified, we will default to the community phone number.
                    </span>
                </Label>
                <div className="grid gap-2">
                    <PhoneNumberInput
                        disabled={
                            isLoading || 
                            !isAdmin  || 
                            settings?.community?.status !== 'active' 
                        }
                        id="alternate_number"
                        name="alternative_callback_forwarding_number"
                        value={alternativePhoneNumber}
                        onChange={handleAlternateNumber}
                        pattern="^(\D*\d\D*){10}$"
                        title="Please enter a valid 10-digit phone number."
                    />
                    <Button
                        className="justify-self-end"
                        onClick={handleSaveNumber}
                        disabled={
                            isLoading || 
                            !isAdmin  || 
                            settings?.community?.status !== 'active' ||
                            alternativePhoneNumber === settings?.alternative_callback_forwarding_number || 
                            (alternativePhoneNumber?.length < 12 && alternativePhoneNumber?.length > 0)
                        }
                    >
                        {isLoading ? "Saving..." : "Save Number"}
                    </Button>
                </div>

            </div>
        </TabsContent>
    )
}